import type { FProductPageTemplateProps } from '@/components/templates/FProductPageTemplate.vue';
import type { KeyFeature } from '@/components/molecules/FKeyFeatureItem.vue';
import type { CaseStudy } from '@/components/organisms/FServiceCaseStudies.vue';
import type { FContactCardProps } from '@/components/molecules/FContactCard.vue';
import type { FaqItem } from '@/components/organisms/FFaq.vue';

export type ProductPageType = 'electric-bike' | 'smart-station';
/**
 * Mapper to get product page name based on their product type
 */
const productTypeToProductPage: Record<ProductType, ProductPageType> = {
  bike: 'electric-bike',
  station: 'smart-station',
};

export interface UseProductPageReturn {
  /**
   * Language-reactive list of props for FProductPageTemplate
   */
  templateProps: ComputedRef<FProductPageTemplateProps>;
}

/**
 * Composable that returns i18n props for FProductPageTemplate
 * @param productType - Page identifier used to build i18n keys
 */
export async function useProductPageProps(
  productType: ProductType
): Promise<UseProductPageReturn> {
  const { t } = useI18n();
  const { pickLocaleWithFallback } = useTranslations();
  const { xsAndDown } = useFBreakpoints();

  // Key features text content
  const keyFeatureItems = computed<KeyFeature[]>(() => [
    {
      text: t(`product__${productType}__key__feature__1`),
    },
    {
      text: t(`product__${productType}__key__feature__2`),
    },
    {
      text: t(`product__${productType}__key__feature__3`),
    },
    {
      text: t(`product__${productType}__key__feature__4`),
    },
  ]);

  // Case studies content with 4 sections
  const caseStudies = computed<CaseStudy[]>(() =>
    new Array(4).fill(null).map((_, index) => {
      const caseStudyIndex = index + 1;
      return {
        city: t(`product__case_study__${caseStudyIndex}_city`),
        title: t(`product__case_study__${caseStudyIndex}_title`),
        text: t(`product__case_study__${caseStudyIndex}_text`),
        image: {
          src:
            caseStudyIndex === 3
              ? `/images/product/case-studies/case-studies-${caseStudyIndex}-${pickLocaleWithFallback(
                  ['es']
                )}-${xsAndDown('mobile', 'desktop')}.jpg`
              : `/images/product/case-studies/case-studies-${caseStudyIndex}-${xsAndDown(
                  'mobile',
                  'desktop'
                )}.jpg`,
          alt: t(`product__case_study__${caseStudyIndex}__image_alt`),
          placeholder:
            caseStudyIndex === 3
              ? `/images/product/case-studies/case-studies-${caseStudyIndex}-${pickLocaleWithFallback(
                  ['es']
                )}-${xsAndDown('mobile', 'desktop')}.placeholder.jpg`
              : `/images/product/case-studies/case-studies-${caseStudyIndex}-${xsAndDown(
                  'mobile',
                  'desktop'
                )}.placeholder.jpg`,
        },
        link: useI18nLink(`product__case_study__${caseStudyIndex}`),
      };
    })
  );

  // Product Turnkey content
  const productTurnkeyServicesCards = computed(() => [
    {
      title: t(`product__services__short_long_duration`),
      location: t(`product__services__short_long_duration_link`),
      titleColor: 'secondary',
      isTrendy: true,
    },
    {
      title: t(`product__services__short_duration`),
      location: t(`product__services__short_duration_link`),
      color: 'info',
      titleColor: 'primary--light-2',
    },
    {
      title: t(`product__services__long_duration`),
      location: t(`product__services__long_duration_link`),
      color: 'success',
      titleColor: 'success--dark-2',
    },
    {
      title: t(`product__services__bike_in_station`),
      location: t(`product__services__bike_in_station_link`),
      color: 'secondary',
      titleColor: 'primary',
    },
  ]);

  // Content of the contact card
  const contactCardProps = computed<FContactCardProps>(() => ({
    title: t('product__contact__title'),
    text: t('product__contact__text'),
    link: useI18nLink('product__contact'),
  }));

  const { data: cmsProductPageData } = await useCmsProductPage(
    productTypeToProductPage[productType]
  );

  // Faq items from cms (Strapi)
  const faqItems: FaqItem[] = cmsProductPageData.value.faqItems;

  const templateProps = computed<FProductPageTemplateProps>(() => ({
    type: productType,
    keyFeatureItems: keyFeatureItems.value,
    caseStudies: caseStudies.value,
    turnkeyServiceCards: productTurnkeyServicesCards.value,
    contactCardProps: contactCardProps.value,
    faqItems: faqItems,
  }));

  return {
    templateProps,
  };
}
